export function initNavigation(header, environment = {}) {
  if (!header) return;
  const doc = environment.document ?? document;
  const finePointer = environment.matchMedia ?? matchMedia;
  const schedule = environment.setTimeout ?? setTimeout;
  const cancel = environment.clearTimeout ?? clearTimeout;
  const menu = header.querySelector('.locations-menu');
  const mobileToggle = header.querySelector('.mobile-nav-toggle');

  // The current navigation intentionally has no Serving/location dropdown.
  // Keep mobile navigation fully functional without requiring legacy menu DOM.
  if (!menu) {
    if (!mobileToggle) return;
    const closeMobile = () => {
      header.dataset.navOpen = 'false';
      mobileToggle.setAttribute('aria-expanded', 'false');
      mobileToggle.setAttribute('aria-label', 'Open menu');
    };
    header.dataset.navReady = 'true';
    mobileToggle.addEventListener('click', () => {
      const next = header.dataset.navOpen !== 'true';
      header.dataset.navOpen = String(next);
      mobileToggle.setAttribute('aria-expanded', String(next));
      mobileToggle.setAttribute('aria-label', next ? 'Close menu' : 'Open menu');
    });
    doc.addEventListener('click', event => {
      if (!header.contains(event.target)) closeMobile();
    });
    doc.addEventListener('keydown', event => {
      if (event.key === 'Escape' && header.dataset.navOpen === 'true') {
        closeMobile();
        mobileToggle.focus();
      }
    });
    return {close: closeMobile};
  }

  const summary = menu.querySelector('summary');
  const panel = menu.querySelector('.locations-panel');
  const stateButtons = [...menu.querySelectorAll('.state-option')];
  const cityPanels = [...menu.querySelectorAll('.state-cities')];
  let closeTimer;
  let openedByHover = false;
  const cancelClose = () => { cancel(closeTimer); closeTimer = undefined; };
  const close = () => { cancelClose(); menu.open = false; openedByHover = false; };
  const open = () => { cancelClose(); menu.open = true; };
  const selected = () => stateButtons.find(button => button.getAttribute('aria-expanded') === 'true');
  const selectState = button => {
    const previous = selected();
    const previousPanel = cityPanels.find(item => !item.hidden);
    const relocateFocus = previous !== button && (doc.activeElement === previous || previousPanel?.contains(doc.activeElement));
    stateButtons.forEach(item => {
      const active = item === button;
      item.setAttribute('aria-expanded', String(active));
      item.tabIndex = active ? 0 : -1;
    });
    cityPanels.forEach(item => { item.hidden = item.id !== button.getAttribute('aria-controls'); });
    if (relocateFocus) button.focus();
  };
  const supportsHover = event => event.pointerType !== 'touch' && finePointer('(hover: hover) and (pointer: fine)').matches;

  menu.addEventListener('pointerenter', event => {
    if (!supportsHover(event)) return;
    if (!menu.open) openedByHover = true;
    open();
  });
  const scheduleHoverClose = () => {
    cancelClose();
    closeTimer = schedule(() => {
      const keyboardFocusInside = Boolean(menu.querySelector(':focus-visible'));
      if (!menu.matches(':hover') && !keyboardFocusInside) close();
    }, 220);
  };
  menu.addEventListener('pointerleave', event => {
    if (!supportsHover(event)) return;
    scheduleHoverClose();
  });
  panel.addEventListener('pointerenter', cancelClose);
  menu.addEventListener('focusin', cancelClose);
  summary.addEventListener('click', event => {
    if (openedByHover && menu.open) event.preventDefault();
    openedByHover = false;
    cancelClose();
  });
  summary.addEventListener('keydown', event => {
    if (!['ArrowDown', 'ArrowUp'].includes(event.key)) return;
    event.preventDefault();
    open();
    const button = event.key === 'ArrowUp' ? stateButtons.at(-1) : selected();
    selectState(button);
    button.focus();
    button.scrollIntoView({block: 'nearest'});
  });
  stateButtons.forEach((button, index) => {
    button.addEventListener('pointerenter', event => { if (supportsHover(event)) selectState(button); });
    button.addEventListener('focus', () => selectState(button));
    button.addEventListener('click', () => selectState(button));
    button.addEventListener('keydown', event => {
      const destinations = {ArrowDown: (index + 1) % stateButtons.length, ArrowUp: (index - 1 + stateButtons.length) % stateButtons.length, Home: 0, End: stateButtons.length - 1};
      if (event.key in destinations) {
        event.preventDefault();
        const next = stateButtons[destinations[event.key]];
        selectState(next);
        next.focus();
        next.scrollIntoView({block: 'nearest'});
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        cityPanels.find(item => !item.hidden)?.querySelector('a')?.focus();
      }
    });
  });
  cityPanels.forEach(item => item.addEventListener('keydown', event => {
    if (event.key === 'ArrowLeft') { event.preventDefault(); selected().focus(); }
  }));
  menu.addEventListener('focusout', event => {
    if (event.relatedTarget && !menu.contains(event.relatedTarget)) close();
  });

  const closeMobile = () => {
    header.dataset.navOpen = 'false';
    mobileToggle.setAttribute('aria-expanded', 'false');
    mobileToggle.setAttribute('aria-label', 'Open menu');
    close();
  };
  header.dataset.navReady = 'true';
  mobileToggle.addEventListener('click', () => {
    const next = header.dataset.navOpen !== 'true';
    header.dataset.navOpen = String(next);
    mobileToggle.setAttribute('aria-expanded', String(next));
    mobileToggle.setAttribute('aria-label', next ? 'Close menu' : 'Open menu');
    if (!next) close();
  });
  doc.addEventListener('click', event => {
    if (!menu.contains(event.target)) close();
    if (!header.contains(event.target)) closeMobile();
  });
  doc.addEventListener('keydown', event => {
    if (event.key !== 'Escape') return;
    if (menu.open) { close(); summary.focus(); }
    else if (header.dataset.navOpen === 'true') { closeMobile(); mobileToggle.focus(); }
  });
  return {close, selectState};
}
